import renderMaskedInput from 'components/FormControls/maskedInputField'
import renderInputField from 'components/FormControls/textInput'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, Form, formValueSelector, reduxForm } from 'redux-form'
import phoneMask from 'utils/constants/phoneMask'
import phoneFieldNormalize from 'utils/normalize/phoneFieldNormalize'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import phoneValidator from 'utils/validators/phoneValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/SubmitButtons'
import clsx from 'clsx'
import formStyles from 'modules/Forms/common-form.module.scss'
import { isRequiredField } from 'utils/isRequiredField'

export const ZeroparkSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    network: Yup.string().required('common::validation_messages.required'),
    email: Yup.string()
      .test('emailValidator', 'common::validation_messages.incorrect_email_format', emailValidator)
      .required('common::validation_messages.required'),
    phone: Yup.string()
      .test('phoneValidator', 'common::validation_messages.incorrect_phone_format', phoneValidator)
      .required('common::validation_messages.required'),
    skype: Yup.string().required('common::validation_messages.required'),
    lastname: Yup.string().required('common::validation_messages.required'),
    firstname: Yup.string().required('common::validation_messages.required'),
  })

let CreateZeroparkAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  formHeader,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
  networkType,
}) => {
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  const { email, user } = useSelector((state) => ({
    email: formValueSelector(form)(state, 'email'),
    user: state['app'].user,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  return (
    <>
      {formHeader}

      <Form
        onSubmit={handleSubmit(submitAccountData)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.zeropark.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="firstname"
            required={isRequiredField(ZeroparkSchema(networkType), 'firstname')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.zeropark.fields.firstname.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="lastname"
            required={isRequiredField(ZeroparkSchema(networkType), 'lastname')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.zeropark.fields.lastname.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            normalize={phoneFieldNormalize}
            required={isRequiredField(ZeroparkSchema(networkType), 'phone')}
            name="phone"
            component={renderMaskedInput}
            placeholderChar={'\u2002'}
            placeholder={'+ ( )'}
            mask={phoneMask}
            label={t('services::forms.register_account.zeropark.fields.phone.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="email"
            required={isRequiredField(ZeroparkSchema(networkType), 'email')}
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.zeropark.fields.email.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="skype"
            required={isRequiredField(ZeroparkSchema(networkType), 'skype')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.zeropark.fields.skype.label')}
            t={t}
          />
        </div>

        <SubmitButtons
          formName={form}
          submitting={submitting}
          onClose={onClose}
        />
      </Form>
    </>
  )
}

CreateZeroparkAccountForm = reduxForm({
  form: 'CreateZeroparkAccountForm',
  initialValues: {
    login: '',
    network: '',
    email: '',
    phone: '',
    lastname: '',
    firstname: '',
    skype: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateZeroparkAccountForm)

CreateZeroparkAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
  networkType: PropTypes.string,
}

export default CreateZeroparkAccountForm
